import {Tab, Tabs} from "@mui/material";
import React from "react";
import {TabWrapper} from "../../shopdrawing/TabWrapper";
import {showNumber} from "../../shopdrawing/pricing/Pricing";
import {PriceDetail} from "../UnPricedItems";
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../api/API";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {useAlternative} from "../alternative/Alternative";
import {SupplierCategoryInternalLabel} from "../../../../api/SupplierCategories";
import {grey} from "@mui/material/colors";

export type HardwareCategory = {
    id: number;
    name: string;
    sortOrder: number;
    internalLabel: SupplierCategoryInternalLabel | null;
};

// useHardwareCategoryList returns the supplier categories that drive the hardware
// subtabs, in sort order. By-us root categories are included; the hardware-provider
// parent is replaced by its Standard/Aluminum hardware children (each always shown,
// even when set to by-others, so the legacy "hardware-riders" / "hardware" notes
// that key to Standard keep a home tab).
export function useHardwareCategoryList(): HardwareCategory[] {
    const project = useProjectId();
    const alternative = useAlternative();

    const get = useAsync2(input => api.supplierCategories.listForProject(input), {
        project: project,
        alternative: alternative,
    }, [project, alternative]);

    const list = get.result || [];
    return list
        .filter(c => {
            // door/frame supplier roots belong to the Doors/Frames sections, not Hardware
            if(c.internalLabel === "door-provider" || c.internalLabel === "frame-provider") return false;
            // the hardware-provider parent is presented as its two children instead
            if(c.internalLabel === "hardware-provider") return false;
            if(c.internalLabel === "standard-hardware-provider" || c.internalLabel === "aluminum-hardware-provider") return true;
            return c.parent === null && c.byUs;
        })
        .map(c => ({id: c.id, name: c.name, sortOrder: c.sortOrder, internalLabel: c.internalLabel}))
        .sort((a, b) => a.sortOrder - b.sortOrder);
}

// the Common subtab combines every hardware price line by product; it sits ahead of
// the per-category subtabs.
export const hardwareCommonTab = "common";

export function hardwareTabValue(categoryId: number) {
    return "cat-" + categoryId;
}

export function parseHardwareTabValue(subTab: string): number | null {
    if(!subTab.startsWith("cat-")) return null;
    const id = parseInt(subTab.slice("cat-".length), 10);
    return isNaN(id) ? null : id;
}

export type HardwareTabDef = {
    value: string;              // "cat-<id>"
    categoryId: number;
    name: string;
    active: boolean;
    unpriced: number;
    internalLabel: SupplierCategoryInternalLabel | null;
};

// buildHardwareTabs returns one tab per by-us root supplier category, unioned with
// any category that actually has priced lines. Categories always show even when
// empty (rendered inactive) so notes/riders can be added.
export function buildHardwareTabs(cats: HardwareCategory[], detail?: PriceDetail): HardwareTabDef[] {
    const byId = new Map<number, HardwareTabDef>();

    cats.forEach(c => byId.set(c.id, {
        value: hardwareTabValue(c.id), categoryId: c.id, name: c.name,
        active: false, unpriced: 0, internalLabel: c.internalLabel,
    }));

    (detail?.hardwareDetail || []).forEach(d => {
        const existing = byId.get(d.category);
        if(existing) {
            existing.active = d.total > 0;
            existing.unpriced = d.unpriced;
        } else {
            byId.set(d.category, {
                value: hardwareTabValue(d.category), categoryId: d.category, name: d.name,
                active: d.total > 0, unpriced: d.unpriced, internalLabel: null,
            });
        }
    });

    return Array.from(byId.values());
}

export function HardwareTabs(props: {
    unpriced?: PriceDetail;
    tab: string;
    onChange(value: string): void;
}) {
    const cats = useHardwareCategoryList();
    const tabs = buildHardwareTabs(cats, props.unpriced);

    return (
        <TabWrapper>
            <Tabs value={props.tab} onChange={(e, value) => props.onChange(value)}>
                <Tab key={hardwareCommonTab} value={hardwareCommonTab} label="Common" />
                {tabs.map(t =>
                    <Tab
                        key={t.value}
                        value={t.value}
                        style={t.active ? undefined : {color: grey["400"]}}
                        label={t.active ? showNumber(t.name, t.unpriced) : t.name}
                    />
                )}
            </Tabs>
        </TabWrapper>
    )
}
